import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import FloatingElements from './FloatingElements';

const DownloadSection = styled.section`
  background-image: url("/images/fondo2.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: "BRLNSDB";
  padding: 100px 0;
  color: white;
  position: relative;
  overflow: hidden;
`;

const DownloadContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h2)`
  font-size: 3rem;
  margin-bottom: 2rem;
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 2rem;
`;

const Card = styled(motion.div)`
  position: relative;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0);
  transition: transform 0.3s ease;
  animation: float 3s ease-in-out infinite;

  &:hover {
    transform: translateY(-10px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }
`;

const CardBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.1);
  }
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  z-index: 2;
  text-align: left;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  color: #333;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  margin: 1rem 0;
  border: 2px solid #eee;
  border-radius: 10px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #667eea;
    outline: none;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    opacity: 0.9;
  }
`;

const FloatingShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  font-size: ${props => props.size || '2rem'};
  color: rgba(255, 255, 255, 0.2);
  user-select: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Shape = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
`;

const Particle = styled(motion.div)`
  position: absolute;
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  pointer-events: none;
`;

const Loader = styled.div`
    width: 48px;
    height: 48px;
    border: 5px solid #FFF;
    border-bottom-color: #4395CA;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
    margin: 20px auto;
    display: block;

    @keyframes rotation {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const Download = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState('');
    const [code, setCode] = useState('');
    const [particles, setParticles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const createParticles = (x, y) => {
        const newParticles = [];
        for (let i = 0; i < 8; i++) {
            const angle = (i * Math.PI * 2) / 8;
            newParticles.push({
                id: `particle-${Date.now()}-${i}`,
                x,
                y,
                angle,
            });
        }
        setParticles([...particles, ...newParticles]);
        setTimeout(() => {
            setParticles(prev => prev.filter(p => !newParticles.includes(p)));
        }, 1000);
    };

    const handleElementClick = (index, event) => {
        const element = document.getElementById(`floating-element-${index}`);
        if (element) {
            element.style.transform = 'scale(1.5)';
            setTimeout(() => {
                element.style.transform = 'scale(1)';
            }, 500);

            // Crear partículas en la posición del clic
            const rect = element.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            createParticles(x, y);
        }
    };

    const handleCardClick = (version) => {
        setSelectedVersion(version);
        setModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) {
            Swal.fire({
                title: '¡Atención!',
                text: 'Por favor, ingresa un código.',
                icon: 'warning',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#4395CA',
                background: '#fff',
                customClass: {
                    title: 'swal2-title',
                    content: 'swal2-content',
                    confirmButton: 'swal2-confirm'
                }
            });
            return;
        }
        
        setIsLoading(true);
    
        try {
            const response = await fetch(
                "http://127.0.0.1:8000/api/verificar-codigo",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ codigo: code }),
                }
            );
    
            const data = await response.json();
    
            if (data.success) {
                await Swal.fire({
                    title: '¡Código válido!',
                    text: 'Tu código ha sido verificado correctamente.',
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonColor: '#4395CA',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Descargar APK',
                    cancelButtonText: 'Cancelar',
                    background: '#fff',
                    customClass: {
                        title: 'swal2-title',
                        content: 'swal2-content',
                        confirmButton: 'swal2-confirm',
                        cancelButton: 'swal2-cancel'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.href = data.apk_url;
                    }
                });
            } else {
                Swal.fire({
                    title: '¡Error!',
                    text: 'El código ingresado no es válido. Por favor, intenta nuevamente.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: '#4395CA',
                    background: '#fff',
                    customClass: {
                        title: 'swal2-title',
                        content: 'swal2-content',
                        confirmButton: 'swal2-confirm'
                    }
                });
            }
        } catch (error) {
            console.error("Error verificando el código:", error);
            Swal.fire({
                title: '¡Error!',
                text: 'Ocurrió un error al verificar el código. Por favor, intenta nuevamente.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#4395CA',
                background: '#fff',
                customClass: {
                    title: 'swal2-title',
                    content: 'swal2-content',
                    confirmButton: 'swal2-confirm'
                }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const downloadVersions = [
        {
            title: 'Versión para estudiantes de primaria',
            description: 'Acceso a contenidos educativos y actividades interactivas',
            image: '/images/pedigital-lite-primaria.png',
            version: 'primaria'
        },
        {
            title: 'Versión para estudiantes de secundaria',
            description: 'Acceso a contenidos educativos y actividades interactivas',
            image: '/images/pedigital-lite-secundaria.png',
            version: 'secundaria'
        },
        {
            title: 'Versión etnopedagógica',
            description: 'Acceso a contenidos educativos y actividades interactivas',
            image: '/images/etnoped.png',
            version: 'etnopedagógica'
        }
    ];

    const educationalElements = [
        { icon: '📚', size: '3rem' },
        { icon: '✏️', size: '2.5rem' },
        { icon: '🎨', size: '2.8rem' },
        { icon: '🔬', size: '3rem' },
        { icon: '🎯', size: '2.7rem' },
        { icon: '💡', size: '2.6rem' },
        { icon: '🎮', size: '2.8rem' },
        { icon: '🧩', size: '3rem' },
        { icon: '📱', size: '2.5rem' },
        { icon: '🌟', size: '2.4rem' },
        { icon: '🎓', size: '2.8rem' },
        { icon: '📝', size: '2.6rem' }
    ];

    return (
        <DownloadSection id="download">
            <FloatingElements section="download" />
            <DownloadContainer ref={ref}>
                <Title
                    initial={{ opacity: 0, y: 50 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    Descarga Nuestra App
                </Title>
                <Description
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    Elige la versión que mejor se adapte a tus necesidades y comienza tu viaje educativo hoy mismo.
                </Description>

                <CardsContainer>
                    {downloadVersions.map((version, index) => (
                        <Card
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: index * 0.2 }}
                            onClick={() => handleCardClick(version.version)}
                        >
                            <CardBackground image={version.image} />
                            <CardContent>

                            </CardContent>
                        </Card>
                    ))}
                </CardsContainer>
            </DownloadContainer>

            <AnimatePresence>
                {modalOpen && (
                    <Modal
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <ModalContent
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                        >
                            <CloseButton onClick={() => setModalOpen(false)}>✕</CloseButton>
                            <h2>Ingresa tu código de acceso</h2>
                            <p>Para descargar la {selectedVersion === 'student' ? 'versión de estudiante' : selectedVersion === 'teacher' ? 'versión de docente' : 'versión institucional'}, por favor ingresa el código que te fue proporcionado.</p>
                            <form onSubmit={handleSubmit}>
                                <Input
                                    type="password"
                                    placeholder="Ingresa tu código aquí"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    disabled={isLoading}
                                />
                                {isLoading ? (
                                    <Loader />
                                ) : (
                                    <SubmitButton
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        type="submit"
                                    >
                                        Validar y Descargar
                                    </SubmitButton>
                                )}
                            </form>
                        </ModalContent>
                    </Modal>
                )}
            </AnimatePresence>
        </DownloadSection>
    );
};

export default Download; 